import { type FormEvent, useEffect, useState } from "react";

import { api } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { LANGS, LANG_LABEL, useLang } from "../lib/i18n";
import { authCopy } from "../lib/i18n/auth";
import { readReferralCode, writeReferralCode } from "../lib/referral";
import { ScreenShell } from "./screen-shell";
import { Eyebrow, FormField, H1, Input, MonoMeta, PrimaryButton } from "./ui/primitives";

type Mode = "sign-in" | "sign-up";

type Props = {
  initialMode?: Mode;
  onForgotPassword: () => void;
  onOpenPrivacy: () => void;
  onOpenTerms: () => void;
};

export function SignInScreen({
  initialMode = "sign-in",
  onForgotPassword,
  onOpenPrivacy,
  onOpenTerms,
}: Props) {
  const { signIn, signUp } = useAuth();
  const { lang, setLang } = useLang();
  const t = authCopy[lang];
  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [inviteCode, setInviteCode] = useState(() => readReferralCode() ?? "");
  const [signupOpen, setSignupOpen] = useState(true);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Probe whether SIGNUP_INVITE_CODES is set so we know whether to
    // prompt for a code. Fail-open (assume open) on network error.
    let cancelled = false;
    api
      .signupMode()
      .then((res) => {
        if (!cancelled) setSignupOpen(res.open);
      })
      .catch(() => {
        // ignore
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setPendingMessage(null);
    if (mode === "sign-up" && !acceptTerms) {
      setError(t.mustAcceptTermsError);
      return;
    }
    setSubmitting(true);
    try {
      if (mode === "sign-in") {
        await signIn(email, password);
      } else {
        const res = await signUp(
          email,
          password,
          name.trim() || undefined,
          inviteCode.trim() || undefined,
          lang,
        );
        if (res.kind === "pending") {
          setPendingMessage(t.pendingVerification);
          setMode("sign-in");
        }
        // Code is consumed (server-side) — clear the LP referral so the
        // user doesn't re-use it later in the tab.
        writeReferralCode(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t.genericError);
    } finally {
      setSubmitting(false);
    }
  }

  const isSignUp = mode === "sign-up";

  return (
    <ScreenShell hideHeader>
      <div style={{ paddingTop: 28, paddingBottom: 12 }}>
        <Eyebrow>{isSignUp ? t.eyebrowSignUp : t.eyebrowSignIn}</Eyebrow>
        <H1 accent="." className="mt-3">
          {isSignUp ? t.headingSignUp : t.headingSignIn}
        </H1>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3" style={{ marginTop: 10 }}>
        {isSignUp ? (
          <FormField label={t.langFieldLabel}>
            <div className="flex gap-2">
              {LANGS.map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => setLang(l)}
                  aria-pressed={l === lang}
                  className="flex-1 text-[13px] font-bold"
                  style={{
                    padding: "10px 0",
                    borderRadius: 10,
                    fontFamily: "var(--font-mono)",
                    background: l === lang ? "var(--ink)" : "var(--surface)",
                    color: l === lang ? "var(--bg)" : "var(--ink-2)",
                    boxShadow: l === lang ? "none" : "inset 0 0 0 1px var(--nav-border)",
                  }}
                >
                  {LANG_LABEL[l]}
                </button>
              ))}
            </div>
          </FormField>
        ) : null}

        {isSignUp ? (
          <FormField label={t.nameLabel}>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              placeholder={t.namePlaceholder}
            />
          </FormField>
        ) : null}

        <FormField label={t.emailLabel}>
          <Input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t.emailPlaceholder}
          />
        </FormField>

        <FormField label={t.passwordLabel} hint={isSignUp ? t.passwordHint : undefined}>
          <Input
            type="password"
            required
            minLength={isSignUp ? 8 : 1}
            autoComplete={isSignUp ? "new-password" : "current-password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            style={{ fontFamily: "var(--font-mono)", letterSpacing: "0.1em" }}
          />
        </FormField>

        {isSignUp && !signupOpen ? (
          <FormField label={t.inviteCodeLabel} hint={t.inviteCodeHint}>
            <Input
              type="text"
              required
              autoComplete="off"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
              placeholder={t.inviteCodePlaceholder}
              style={{ fontFamily: "var(--font-mono)", letterSpacing: "0.1em" }}
            />
          </FormField>
        ) : null}

        {!isSignUp ? (
          <div className="text-right" style={{ marginTop: -4 }}>
            <button
              type="button"
              onClick={onForgotPassword}
              className="text-[12px] font-semibold"
              style={{ color: "var(--accent)" }}
            >
              {t.forgotPasswordLink}
            </button>
          </div>
        ) : null}

        {isSignUp ? (
          <div
            className="flex flex-col gap-3 p-3 rounded-lg"
            style={{ background: "var(--surface-2)" }}
          >
            <label className="flex items-start gap-2 text-[13px] cursor-pointer">
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                className="w-4 h-4 mt-0.5 shrink-0 accent-[var(--accent)]"
                required
              />
              <span style={{ color: "var(--ink-2)" }}>
                {t.acceptTermsPrefix}
                <button
                  type="button"
                  onClick={onOpenPrivacy}
                  className="underline font-semibold"
                  style={{ color: "var(--accent)" }}
                >
                  {t.acceptTermsPrivacy}
                </button>
                {t.acceptTermsAnd}
                <button
                  type="button"
                  onClick={onOpenTerms}
                  className="underline font-semibold"
                  style={{ color: "var(--accent)" }}
                >
                  {t.acceptTermsTerms}
                </button>
                {t.acceptTermsSuffix}
              </span>
            </label>
          </div>
        ) : null}

        {error ? (
          <p
            className="text-[12px] px-3 py-2 rounded-md"
            style={{ background: "var(--bad-bg)", color: "var(--bad-fg)" }}
          >
            {error}
          </p>
        ) : null}

        {pendingMessage ? (
          <p
            className="text-[12px] px-3 py-2 rounded-md"
            style={{ background: "var(--ok-bg)", color: "var(--ok-fg)" }}
          >
            {pendingMessage}
          </p>
        ) : null}

        <PrimaryButton type="submit" disabled={submitting} className="mt-1">
          {submitting ? t.submitting : isSignUp ? t.submitSignUp : t.submitSignIn}
        </PrimaryButton>

        <div className="flex justify-center mt-2">
          <button
            type="button"
            onClick={() => setMode(isSignUp ? "sign-in" : "sign-up")}
            className="text-[13px]"
            style={{ color: "var(--ink-2)" }}
          >
            {isSignUp ? t.haveAccountQuestion : t.noAccountQuestion}
            <span style={{ color: "var(--accent)", fontWeight: 700 }}>
              {isSignUp ? t.switchToSignIn : t.switchToSignUp}
            </span>
          </button>
        </div>
      </form>

      <footer className="flex justify-center gap-4 mt-auto" style={{ paddingTop: 32 }}>
        <button
          type="button"
          onClick={onOpenPrivacy}
          className="underline"
          style={{ color: "var(--muted)" }}
        >
          <MonoMeta>{t.footerPrivacy}</MonoMeta>
        </button>
        <button
          type="button"
          onClick={onOpenTerms}
          className="underline"
          style={{ color: "var(--muted)" }}
        >
          <MonoMeta>{t.footerTerms}</MonoMeta>
        </button>
      </footer>
    </ScreenShell>
  );
}
